import {
  InfiniteData, keepPreviousData, QueryClient, skipToken, useInfiniteQuery, useMutation, useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import taskService from '../services/task.service';
import {
  CreateTaskData, PaginatedTasks, Task, TaskApiParams, TaskViewKey, UpdateTaskData,
} from '../types/task.types';

const STALE_MS = 30_000;

export type TaskGroupKey =
  | 'overdue' | 'in-progress' | 'pending' | 'completed' | 'cancelled' | 'awaiting-approval'
  | 'archived';

export interface TaskGroupDef {
  key: TaskGroupKey;
  label: string;
  params: TaskApiParams;
}

// The backend's overdue filter only gates on is_complete, so without the
// status__in a past-due CANCELLED task would be pinned as overdue.
const ACTIVE_STATUSES = 'TODO,IN_PROGRESS,REVIEW';

export const GROUP_OVERDUE: TaskGroupDef = {
  key: 'overdue', label: 'Overdue', params: { overdue: 'true', status__in: ACTIVE_STATUSES },
};
// IN_PROGRESS only. This used to fold REVIEW in as well, so the same filter
// gave a different In progress count on the list than on the board, which
// splits REVIEW into its own column, and than on the dashboard KPI, which
// counts IN_PROGRESS alone.
export const GROUP_IN_PROGRESS: TaskGroupDef = {
  key: 'in-progress', label: 'In progress',
  params: { status: 'IN_PROGRESS', exclude_overdue: 'true' },
};
export const GROUP_PENDING: TaskGroupDef = {
  key: 'pending', label: 'Pending', params: { status: 'TODO', exclude_overdue: 'true' },
};
export const GROUP_COMPLETED: TaskGroupDef = {
  key: 'completed', label: 'Completed', params: { status: 'COMPLETED' },
};
export const GROUP_CANCELLED: TaskGroupDef = {
  key: 'cancelled', label: 'Cancelled', params: { status: 'CANCELLED' },
};
// Two shapes of the same bucket. The list slot drops overdue rows, which the
// Overdue group above it has already claimed. The dedicated view is the only
// place a REVIEW task appears, so it must keep the overdue ones.
export const GROUP_AWAITING_ACTIVE: TaskGroupDef = {
  key: 'awaiting-approval', label: 'Awaiting approval',
  params: { status: 'REVIEW', exclude_overdue: 'true' },
};
export const GROUP_AWAITING: TaskGroupDef = {
  key: 'awaiting-approval', label: 'Awaiting approval', params: { status: 'REVIEW' },
};

// Archived tasks are excluded from every other list, so without this the
// restore path is unreachable from the screen.
export const GROUP_ARCHIVED: TaskGroupDef = {
  key: 'archived', label: 'Archived',
  params: { status: 'ARCHIVED', include_archived: 'true' },
};

export const COL_PENDING: TaskGroupDef = { key: 'pending', label: 'Pending', params: { status: 'TODO' } };
export const COL_IN_PROGRESS: TaskGroupDef = {
  key: 'in-progress', label: 'In progress', params: { status: 'IN_PROGRESS' },
};
export const COL_REVIEW: TaskGroupDef = {
  key: 'awaiting-approval', label: 'Awaiting approval', params: { status: 'REVIEW' },
};
export const COL_COMPLETED: TaskGroupDef = {
  key: 'completed', label: 'Completed', params: { status: 'COMPLETED' },
};

export function groupParams(view: TaskViewKey, group: TaskGroupDef, search: string): TaskApiParams {
  const base: TaskApiParams = view === 'mine' ? { assigned_to_me: 'true' } : {};
  const params: TaskApiParams = { ...base, ...group.params, ordering: 'due_date' };
  if (search) params.search = search;
  return params;
}

export function boardParams(view: TaskViewKey, column: TaskGroupDef, search: string): TaskApiParams {
  // 'awaiting-approval' adds nothing: the board is already partitioned by
  // status and its Awaiting approval column IS that view.
  const base: TaskApiParams =
    view === 'mine' ? { assigned_to_me: 'true' } : view === 'overdue' ? { overdue: 'true' } : {};
  const params: TaskApiParams = { ...base, ...column.params, ordering: 'due_date' };
  if (search) params.search = search;
  return params;
}

const pageFromNext = (next: string | null): number | undefined => {
  if (!next) return undefined;
  const page = new URL(next, window.location.origin).searchParams.get('page');
  return page ? Number(page) : undefined;
};

export interface TaskGroup {
  key: TaskGroupKey;
  label: string;
  rows: Task[];
  count: number;
  isPending: boolean;
  isError: boolean;
  hasMore: boolean;
  isFetchingMore: boolean;
  fetchMore: () => void;
}

function useTaskGroup(def: TaskGroupDef, params: TaskApiParams, enabled: boolean): TaskGroup {
  const query = useInfiniteQuery({
    queryKey: ['tasks-group', def.key, params],
    queryFn: ({ pageParam }) => taskService.getTasksPage({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last: PaginatedTasks) => pageFromNext(last.next),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
    enabled,
  });
  return {
    key: def.key,
    label: def.label,
    rows: query.data?.pages.flatMap((page) => page.results) ?? [],
    count: query.data?.pages[0]?.count ?? 0,
    isPending: query.isPending,
    isError: query.isError,
    hasMore: query.hasNextPage ?? false,
    isFetchingMore: query.isFetchingNextPage,
    fetchMore: () => query.fetchNextPage(),
  };
}

export function useListGroups(view: TaskViewKey, search: string, enabled: boolean): TaskGroup[] {
  const isAwaiting = view === 'awaiting-approval';
  const isOverdueView = view === 'overdue';
  const isArchived = view === 'archived';
  // Hook order must stay fixed across view switches: all six group slots
  // always run; the awaiting-approval view reuses the in-progress slot and the
  // others are disabled and dropped from the result.
  // The archived view reuses the same slot the awaiting view does, so the
  // hook count stays fixed across a view switch.
  const inProgressDef = isArchived
    ? GROUP_ARCHIVED
    : isAwaiting
      ? GROUP_AWAITING
      : GROUP_IN_PROGRESS;
  const overdue = useTaskGroup(
    GROUP_OVERDUE, groupParams(view, GROUP_OVERDUE, search),
    enabled && !isAwaiting && !isArchived,
  );
  const inProgress = useTaskGroup(
    inProgressDef, groupParams(view, inProgressDef, search), enabled && !isOverdueView,
  );
  const rest = enabled && !isAwaiting && !isOverdueView && !isArchived;
  const awaiting = useTaskGroup(
    GROUP_AWAITING_ACTIVE, groupParams(view, GROUP_AWAITING_ACTIVE, search), rest,
  );
  const pending = useTaskGroup(GROUP_PENDING, groupParams(view, GROUP_PENDING, search), rest);
  const completed = useTaskGroup(GROUP_COMPLETED, groupParams(view, GROUP_COMPLETED, search), rest);
  const cancelled = useTaskGroup(GROUP_CANCELLED, groupParams(view, GROUP_CANCELLED, search), rest);
  if (isOverdueView) return [overdue];
  if (isAwaiting || isArchived) return [inProgress];
  return [overdue, inProgress, awaiting, pending, completed, cancelled];
}

export function useBoardColumns(view: TaskViewKey, search: string, enabled: boolean): TaskGroup[] {
  const pending = useTaskGroup(COL_PENDING, boardParams(view, COL_PENDING, search), enabled);
  const inProgress = useTaskGroup(COL_IN_PROGRESS, boardParams(view, COL_IN_PROGRESS, search), enabled);
  const review = useTaskGroup(COL_REVIEW, boardParams(view, COL_REVIEW, search), enabled);
  const completed = useTaskGroup(COL_COMPLETED, boardParams(view, COL_COMPLETED, search), enabled);
  return [pending, inProgress, review, completed];
}

export function useTaskDetail(taskId: string | null) {
  return useQuery({
    queryKey: ['task-detail', taskId],
    queryFn: taskId == null ? skipToken : () => taskService.getTask(taskId),
    staleTime: STALE_MS,
  });
}

type RowSnapshot = Array<[readonly unknown[], Task]>;

// The register peek lists a risk's treatment tasks ('risk-tasks'); the
// dashboard KpiStrip has its own task-count query ('task-stats').
export function invalidateTaskFamilies(queryClient: QueryClient) {
  ['tasks-group', 'task-detail', 'attention-tasks', 'recent-activities', 'risk-tasks', 'task-stats']
    .forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
}

function useOptimisticTaskPatch() {
  const queryClient = useQueryClient();
  const apply = async (id: string, patch: Partial<Task>): Promise<RowSnapshot> => {
    await queryClient.cancelQueries({ queryKey: ['tasks-group'] });
    // Snapshot only the mutated row per cache entry — restoring whole cache
    // blobs would also revert other rows' in-flight optimistic patches.
    const snapshot: RowSnapshot = [];
    queryClient
      .getQueriesData<InfiniteData<PaginatedTasks>>({ queryKey: ['tasks-group'] })
      .forEach(([key, data]) => {
        const row = data?.pages.flatMap((page) => page.results).find((task) => task.id === id);
        if (row) snapshot.push([key, row]);
      });
    queryClient.setQueriesData<InfiniteData<PaginatedTasks>>({ queryKey: ['tasks-group'] }, (data) => {
      if (!data) return data;
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          results: page.results.map((task) => (task.id === id ? { ...task, ...patch } : task)),
        })),
      };
    });
    return snapshot;
  };
  const rollback = (snapshot: RowSnapshot) => {
    snapshot.forEach(([key, prevRow]) => {
      queryClient.setQueryData<InfiniteData<PaginatedTasks>>(key, (data) => {
        if (!data) return data;
        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            results: page.results.map((task) => (task.id === prevRow.id ? prevRow : task)),
          })),
        };
      });
    });
  };
  const settle = () => invalidateTaskFamilies(queryClient);
  return { apply, rollback, settle };
}

export function useCompleteTask() {
  const patch = useOptimisticTaskPatch();
  return useMutation({
    mutationFn: (id: string) => taskService.completeTask(id),
    onMutate: (id) => patch.apply(id, { status: 'COMPLETED', progress: 100 }),
    onError: (_error, _id, snapshot) => snapshot && patch.rollback(snapshot),
    onSettled: () => patch.settle(),
  });
}

export function useReopenTask() {
  const patch = useOptimisticTaskPatch();
  return useMutation({
    mutationFn: (id: string) => taskService.reopenTask(id),
    onMutate: (id) => patch.apply(id, { status: 'IN_PROGRESS' }),
    onError: (_error, _id, snapshot) => snapshot && patch.rollback(snapshot),
    onSettled: () => patch.settle(),
  });
}

export function useUpdateTaskInline() {
  const patch = useOptimisticTaskPatch();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaskData }) => taskService.updateTask(id, data),
    onMutate: ({ id, data }) => {
      const optimistic = { ...(data as Partial<Task>) };
      if (data.assignee !== undefined) optimistic.assignee = data.assignee ?? '';
      return patch.apply(id, optimistic);
    },
    onError: (_error, _vars, snapshot) => snapshot && patch.rollback(snapshot),
    onSettled: () => patch.settle(),
  });
}

export function useToggleChecklistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => taskService.toggleChecklistItem(itemId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['task-detail'] }),
  });
}

export function useArchiveTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => taskService.archiveTask(id),
    onSuccess: () => invalidateTaskFamilies(queryClient),
  });
}

export function useRestoreTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => taskService.restoreTask(id),
    onSuccess: () => invalidateTaskFamilies(queryClient),
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTaskData) => taskService.createTask(data),
    onSuccess: () => invalidateTaskFamilies(queryClient),
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaskData }) => taskService.updateTask(id, data),
    onSuccess: () => invalidateTaskFamilies(queryClient),
  });
}
